import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TemplateItem } from './BrutalGrid';

interface TemplateRequestModalProps {
  template: TemplateItem | null;
  onClose: () => void;
}

export default function TemplateRequestModal({ template, onClose }: TemplateRequestModalProps) {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const isGeneric = !template?.poster;

  useEffect(() => {
    if (template) {
      setSubmitted(false);
      setError('');
      setCharCount(0);
    }
  }, [template]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!template) return null;

  const handleBuildSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => data.append(key, value.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d7e6ill37cef9t4028kg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal panel */}
      <div
        className="relative bg-white w-full md:max-w-2xl md:rounded-2xl overflow-hidden"
        style={{ maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/10 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-black/5 flex items-center justify-center">
              {template.poster
                ? <img src={template.poster} alt={template.name} className="w-full h-full object-cover object-top" />
                : <i className="ri-hammer-line text-black/40 text-base"></i>
              }
            </div>
            <div>
              <p className="text-xs text-black/40 uppercase tracking-widest">{template.category}</p>
              <h2 className="text-sm font-semibold text-black leading-tight">{template.name}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-black/40 hover:text-black transition-colors cursor-pointer rounded-full hover:bg-black/5"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        {submitted ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-black mb-4">
              <i className="ri-check-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-black mb-2">Request Sent!</h3>
            <p className="text-sm text-black/50 max-w-xs">
              We received your build request for <strong>{template.name}</strong>. We&apos;ll call you shortly.
            </p>
            <button
              onClick={onClose}
              className="mt-8 px-6 py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-6 py-6">

            {/* Template preview — only for real templates */}
            {!isGeneric && (
              <>
                <div
                  className="rounded-xl overflow-hidden mb-3 border border-black/8 relative group cursor-pointer"
                  style={{ aspectRatio: '16/7' }}
                  onClick={() => { onClose(); navigate(`/preview/${template.id}`); }}
                >
                  <img src={template.poster} alt={template.name} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 px-4 py-2 bg-white text-black text-xs font-bold rounded-full flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-eye-line"></i> Preview Template
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => { onClose(); navigate(`/preview/${template.id}`); }}
                  className="w-full mb-5 py-2 border border-black/15 text-black text-xs font-semibold rounded-full hover:bg-black hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                >
                  <i className="ri-eye-line"></i> Preview Full Template
                </button>
              </>
            )}

            {/* Build form */}
            <form data-readdy-form id="template-request-form" onSubmit={handleBuildSubmit} className="flex flex-col gap-4">
              <input type="hidden" name="template_name" value={template.name} />
              <input type="hidden" name="template_category" value={template.category} />
              <input type="hidden" name="template_url" value={template.externalUrl} />
              <input type="hidden" name="request_type" value="Build My Website" />

              <p className="text-xs font-semibold text-black/40 uppercase tracking-widest">Build my website using this style</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">First Name *</label>
                  <input type="text" name="first_name" required placeholder="Jane" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Last Name *</label>
                  <input type="text" name="last_name" required placeholder="Smith" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Email *</label>
                <input type="email" name="email" required placeholder="jane@example.com" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Phone Number *</label>
                <input type="tel" name="phone" required placeholder="+1 (555) 000-0000" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Business / Project Name</label>
                <input type="text" name="business_name" placeholder="My Brand Co." className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Budget Range</label>
                <select name="budget" className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black focus:outline-none focus:border-black transition-colors bg-white cursor-pointer">
                  <option value="">Select a range...</option>
                  <option value="Under $1,000">Under $1,000</option>
                  <option value="$1,000 – $3,000">$1,000 – $3,000</option>
                  <option value="$3,000 – $7,500">$3,000 – $7,500</option>
                  <option value="$7,500 – $15,000">$7,500 – $15,000</option>
                  <option value="$15,000+">$15,000+</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-black/50 uppercase tracking-wider">Tell us about your project</label>
                <textarea
                  name="message"
                  placeholder="Describe your business, goals, and what you need..."
                  maxLength={500}
                  rows={4}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-black/15 rounded-lg px-3 py-2.5 text-sm text-black placeholder-black/30 focus:outline-none focus:border-black transition-colors resize-none"
                />
                <p className="text-xs text-black/30 text-right">{charCount}/500</p>
              </div>
              {error && <p className="text-xs text-red-500 text-center">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-black text-white text-sm font-semibold rounded-full hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Send Build Request'}
              </button>
              <p className="text-xs text-black/30 text-center">We typically call within 24 hours.</p>
            </form>

          </div>
        )}
      </div>
    </div>
  );
}
